<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-8">
					<h3 class="page-header">
						<i class="fa fa-inr animated bounceInDown show-info"></i>
						Attendance Deduction Types
					</h3>
					<p class="text-muted" style="margin-left:4px">
						The reasons money or paid days can move against a day. A fine, a canteen bill and an
						incentive differ only in these columns, so adding one is data entry rather than a release.
					</p>
				</div>
				<div class="col-md-4 text-right" style="margin-top:20px">
					<button class="btn btn-primary" @click="create">
						<i class="fa fa-plus"></i> Add deduction type
					</button>
				</div>
			</div>
		</div>

		<div class="col-md-12" v-if="error" style="margin-top:10px">
			<div class="alert alert-danger">{{ error }}</div>
		</div>

		<div class="col-md-12 table-responsive" style="padding:0px;margin-top:10px" v-else>
			<div class="panel">
				<div class="panel-body table" style="overflow-x:auto;padding-left:0px">
					<table class="table table-bordered table-hover table-striped display">
						<thead class="table-font">
							<th>Type</th>
							<th>Direction</th>
							<th>Measured in</th>
							<th>Default</th>
							<th>Cap / period</th>
							<th>Entry</th>
							<th>Approval</th>
							<th></th>
						</thead>
						<tbody>
							<tr v-if="loading">
								<td colspan="8" class="text-center">Loading…</td>
							</tr>
							<tr v-else-if="!rows.length">
								<td colspan="8" class="text-center">No deduction types configured yet.</td>
							</tr>
							<tr v-for="row in rows" :key="row.id" :class="{ inactive: !row.is_active }">
								<td>
									<span class="color-dot" :style="{ background: row.color || '#94a3b8' }"></span>
									<strong>{{ row.name }}</strong>
									<br />
									<small class="text-muted">{{ row.code }}</small>
								</td>
								<td>
									<span
										class="badge"
										:class="row.direction === 'credit' ? 'bg-success' : 'bg-danger'"
									>
										{{ row.direction === 'credit' ? 'Adds' : 'Deducts' }}
									</span>
								</td>
								<td>{{ unitLabel(row.unit) }}</td>
								<td>{{ Number(row.default_value) || '—' }}</td>
								<td>{{ row.max_per_month ? Number(row.max_per_month) : 'Uncapped' }}</td>
								<td>
									<span class="badge" :class="row.allow_manual ? 'bg-secondary' : 'bg-dark'">
										{{ row.allow_manual ? 'By hand' : 'Policy only' }}
									</span>
								</td>
								<td>
									<span v-if="row.requires_approval" class="badge bg-warning text-dark">
										Needs approval
									</span>
									<span v-else class="text-muted">Immediate</span>
								</td>
								<td style="white-space:nowrap">
									<button class="btn btn-sm btn-primary" @click="edit(row)" title="Edit">
										<i class="fa fa-pencil"></i>
									</button>
									<button class="btn btn-sm btn-danger" @click="remove(row)" title="Deactivate">
										<i class="fa fa-trash"></i>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<modal name="attendance-deductiontype" :width="680" :height="'auto'" :adaptive="true" :scrollable="true">
			<div style="padding:18px;max-height:80vh;overflow-y:auto">
				<h4 class="modal-header-title">
					{{ form.id ? 'Edit deduction type' : 'Add deduction type' }}
				</h4>

				<div class="row">
					<div class="col-md-4 form-group">
						<label>Code</label>
						<input type="text" class="form-control form-cascade-control" placeholder="LATE" v-model="form.code" />
					</div>
					<div class="col-md-8 form-group">
						<label>Name</label>
						<input type="text" class="form-control form-cascade-control" placeholder="Late arrival fine" v-model="form.name" />
					</div>
				</div>

				<div class="row">
					<div class="col-md-4 form-group">
						<label>Colour</label>
						<input type="color" class="form-control form-cascade-control" v-model="form.color" />
					</div>
					<div class="col-md-4 form-group">
						<label>Direction</label>
						<select class="form-control form-cascade-control" v-model="form.direction">
							<option value="debit">Deducts — takes away</option>
							<option value="credit">Adds — incentive, bonus, arrears</option>
						</select>
					</div>
					<div class="col-md-4 form-group">
						<label>Measured in</label>
						<select class="form-control form-cascade-control" v-model="form.unit">
							<option value="amount">Money</option>
							<option value="days">Paid days</option>
							<option value="hours">Hours</option>
						</select>
					</div>
				</div>

				<div class="row">
					<div class="col-md-6 form-group">
						<label>Default value</label>
						<input type="number" min="0" step="0.01" class="form-control form-cascade-control" v-model.number="form.default_value" />
						<small class="text-muted">Prefills the form. It can still be changed per entry.</small>
					</div>
					<div class="col-md-6 form-group">
						<label>Cap per employee per period</label>
						<input type="number" min="0" step="0.01" class="form-control form-cascade-control" v-model.number="form.max_per_month" />
						<small class="text-muted">
							Leave blank for uncapped. A ceiling stops a slipped decimal taking a whole salary.
						</small>
					</div>
				</div>

				<hr />
				<p class="font-weight-bold">Rules</p>

				<div class="checkbox" v-if="form.unit === 'days'">
					<label>
						<input type="checkbox" v-model="form.affects_payable" />
						Reduce payable days — payroll pays for fewer days, not just a note on the record
					</label>
				</div>
				<div class="checkbox">
					<label>
						<input type="checkbox" v-model="form.requires_note" />
						Require a reason when this is applied
					</label>
				</div>
				<div class="checkbox">
					<label>
						<input type="checkbox" v-model="form.requires_approval" />
						Hold for approval before it counts
					</label>
				</div>
				<div class="checkbox">
					<label>
						<input type="checkbox" v-model="form.allow_manual" />
						An admin can add this by hand — switch off for policy-generated types
					</label>
				</div>

				<div class="row">
					<div class="col-md-6 form-group">
						<label>Sort order</label>
						<input type="number" min="0" max="999" class="form-control form-cascade-control" v-model.number="form.sort_order" />
					</div>
					<div class="col-md-6">
						<div class="checkbox" style="margin-top:28px">
							<label><input type="checkbox" v-model="form.is_active" /> Active</label>
						</div>
					</div>
				</div>

				<div class="text-right" style="margin-top:12px">
					<button class="btn btn-default" @click="$modal.hide('attendance-deductiontype')">Cancel</button>
					<button class="btn btn-primary" :disabled="saving" @click="save">Save</button>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
import { deductionTypeService, errorMessage } from './attendanceService'

const UNIT_LABELS = {
	amount: 'Money',
	days: 'Paid days',
	hours: 'Hours',
}

export default {
	name: 'AttendanceDeductionTypes',
	data() {
		return {
			rows: [],
			loading: false,
			error: '',
			saving: false,
			form: this.blank(),
		}
	},
	mounted() {
		this.refresh()
	},
	methods: {
		blank() {
			return {
				id: null,
				code: '',
				name: '',
				color: '#c0392b',
				unit: 'amount',
				direction: 'debit',
				default_value: 0,
				affects_payable: false,
				requires_note: true,
				requires_approval: false,
				max_per_month: null,
				allow_manual: true,
				sort_order: 0,
				is_active: true,
			}
		},
		unitLabel(unit) {
			return UNIT_LABELS[unit] || unit
		},
		refresh() {
			this.loading = true
			this.error = ''
			deductionTypeService
				.list()
				.then((data) => {
					this.rows = data || []
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load deduction types.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		create() {
			this.form = this.blank()
			this.$modal.show('attendance-deductiontype')
		},
		edit(type) {
			this.form = { ...type }
			this.$modal.show('attendance-deductiontype')
		},
		save() {
			this.saving = true
			const body = { ...this.form }
			const id = body.id
			delete body.id
			const req = id ? deductionTypeService.update(id, body) : deductionTypeService.create(body)
			req
				.then(() => {
					this.$notify({ text: 'Saved', type: 'success' })
					this.$modal.hide('attendance-deductiontype')
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Could not save'), type: 'error' })
				})
				.finally(() => {
					this.saving = false
				})
		},
		remove(type) {
			if (
				!window.confirm(
					`Deactivate "${type.name}"? Deductions already charged under it are kept.`
				)
			)
				return
			deductionTypeService
				.remove(type.id)
				.then(() => {
					this.$notify({ text: 'Deactivated', type: 'success' })
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Failed'), type: 'error' })
				})
		},
	},
}
</script>

<style scoped>
.modal-header-title {
	margin-top: 0;
}
.color-dot {
	display: inline-block;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	margin-right: 6px;
	vertical-align: middle;
}
.inactive td {
	opacity: 0.55;
}
</style>
